import { isValidElement } from "react";
import {
  Cart,
  Link,
  Person,
  PersonWorkspace,
  Stripe,
} from "react-bootstrap-icons";
import { Badge } from "@components";

const getMonth = (offset = 0) => {
  const today = new Date();
  const currentDay = today.getDate();

  // Move to the 1st of the month to avoid overflow issues
  const newDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);

  // Get the last valid day of the target month
  const lastDay = new Date(
    newDate.getFullYear(),
    newDate.getMonth() + 1,
    0
  ).getDate();

  // Restore the original day, clamped to the last day of the month
  newDate.setDate(Math.min(currentDay, lastDay));

  return new Intl.DateTimeFormat("en-GB", { month: "long" }).format(newDate);
};

const formatPaymentSource = (x) => {
  let Icon = (function () {
    switch (x) {
      case "Ecommerce checkout":
        return Cart;
      case "Payment request":
        return Link;
      case "Trade web portal":
        return PersonWorkspace;
      case "Trade payment link":
        return Person;
      case "Stripe":
        return Stripe;
      default:
        return Person;
    }
  })();

  return (
    <Badge color="zinc">
      <Icon className="me-2" size={12} />
      <span>{x}</span>
    </Badge>
  );
};

function formatDate(inputDateString) {
  if (!inputDateString) return "";
  // Parse the input date string into a Date object
  const inputDate = new Date(inputDateString);
  const day = String(inputDate.getDate()).padStart(2, "0");
  const month = String(inputDate.getMonth() + 1).padStart(2, "0"); // Month is 0-based
  const year = inputDate.getFullYear().toString().substring(2, 4);

  return `${day}.${month}.${year}`;
}

function formatDateLong(inputDateString) {
  if (!inputDateString) return "";

  const inputDate = new Date(inputDateString);

  return inputDate
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    })
    .replace(",", "");
}

function formatDateShortMonth(inputDateString) {
  if (!inputDateString) return "";
  const inputDate = new Date(inputDateString);
  return inputDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

function applyCountryCode(phone) {
  if (phone && phone[0] === "0") {
    let countryPhone = phone.substring(1);
    countryPhone = `+44${countryPhone}`;
    return countryPhone;
  }

  return phone;
}

const yesterday = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday;
};

function formatDateTime(dateString, dateTimeSplitter) {
  if (!dateString) {
    return "";
  }

  const date = new Date(dateString);
  const today = new Date();

  if (date.toDateString() === today.toDateString()) {
    return `Today${dateTimeSplitter ?? ", "}${date.toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else if (date.toDateString() === yesterday().toDateString()) {
    return `Yesterday${dateTimeSplitter ?? ", "}${date.toLocaleString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } else {
    const datePart = date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
    const timePart = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datePart}${dateTimeSplitter ?? ", "}${timePart}`;
  }
}

// Local-time timestamp for CSV exports. The API returns UTC ISO strings; writing those straight
// into the file stamps every row in UTC, which reads wrong to a user in any other zone. Uses the
// browser's zone and a fixed, sortable `dd/MM/yyyy HH:mm:ss` rather than formatDateTime's
// "Today, 14:32" shorthand, which is meaningless once the file leaves the screen.
function formatDateTimeForExport(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

function formatCountdown(inputDateString) {
  if (!inputDateString) return null;

  // Parse the input date string into a Date object
  const inputDate = new Date(inputDateString);
  var today = new Date();

  // Check if inputDate is valid
  if (isNaN(inputDate)) return null;

  // Set the time of 'today' to 00:00:00 for accurate day comparison
  today.setHours(0, 0, 0, 0);

  // Calculate the difference in days
  const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
  const diffDays = Math.floor((inputDate - today) / oneDay);

  if (diffDays > 1) {
    // More than one day difference
    return diffDays + " days";
  } else if (diffDays === 1) {
    // Input date is tomorrow
    return "tomorrow";
  } else if (diffDays === 0 && inputDate > new Date()) {
    // Same day, but future time
    return (
      "today at " +
      inputDate.getHours() +
      ":" +
      (inputDate.getMinutes() < 10 ? "0" : "") +
      inputDate.getMinutes()
    );
  }

  return null;
}

function formatTimeAgo(inputDateString) {
  if (!inputDateString) return null;

  // Parse the input date string into a Date object
  const inputDate = new Date(inputDateString);
  const today = new Date();

  // Check if inputDate is valid
  if (isNaN(inputDate)) return null;

  // Set the time of 'today' to 00:00:00 for accurate day comparison
  today.setHours(0, 0, 0, 0);

  // Set inputDate time to 00:00:00 as well for day-based comparison
  inputDate.setHours(0, 0, 0, 0);

  // Calculate the difference in days
  const oneDay = 24 * 60 * 60 * 1000; // milliseconds in a day
  const diffDays = Math.floor((today - inputDate) / oneDay);

  if (diffDays === 0) {
    // Input date is today
    return "today";
  } else if (diffDays === 1) {
    // Input date was yesterday
    return "yesterday";
  } else if (diffDays < 30) {
    // Input date was within the last month, return in days
    return diffDays + " days ago";
  } else {
    // Calculate the difference in months
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMonths === 1) {
      // Input date was last month
      return "last month";
    } else {
      // Input date was more than one month ago
      return diffMonths + " months ago";
    }
  }
}

function formatCommMethod(input, number) {
  if (!input) {
    return null;
  }

  switch (input.toLowerCase()) {
    case "sms":
      return `SMS ${number}`;
    case "qrcode":
      return `QR code`;
    case "link":
      return `URL`;
    default:
      return null;
  }
}

function formatAmount(inputAmountString, trimSymbol = false) {
  if (!inputAmountString && inputAmountString !== 0) {
    return null;
  }

  // Parse the input amount string to a floating-point number
  const inputAmount = parseFloat(inputAmountString);

  // Format the amount with the currency symbol (£) and desired formatting
  const formattedAmount = inputAmount.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Display or use the formatted amount
  return trimSymbol ? formattedAmount.substring(1) : formattedAmount; // Output: "£246,120.32"
}

function normalizeAmountValue(amount) {
  if (amount === null || amount === undefined || amount === "") {
    return formatAmount(0);
  }

  if (typeof amount === "number") {
    return formatAmount(amount);
  }

  if (typeof amount === "string") {
    const trimmedAmount = amount.trim();
    const isFormatted = /[£$€,]/g.test(trimmedAmount);

    if (isFormatted) {
      return trimmedAmount;
    } else {
      const numericValue = parseFloat(trimmedAmount);
      if (isNaN(numericValue)) {
        return formatAmount(0);
      }
      return formatAmount(numericValue);
    }
  }

  return formatAmount(parseFloat(amount) || 0);
}

function formatKebabCase(kebabStr) {
  if (!kebabStr) return "";

  return kebabStr
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatPascalCase(pString) {
  if (!pString) return "";

  return (
    pString
      // Split the string at each point a lowercase to uppercase transition occurs
      .split(/(?=[A-Z])/)
      // Convert the first character of each word to uppercase, and the rest to lowercase
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      // Join the words with a space
      .join(" ")
  );
}

function formatSnakeCase(snakeCaseStr) {
  if (!snakeCaseStr) return "";

  return snakeCaseStr
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatSortCode(sortCode) {
  if (!sortCode) return "";

  return (
    sortCode.substring(0, 2) +
    "-" +
    sortCode.substring(2, 4) +
    "-" +
    sortCode.substring(4, 6)
  );
}

function formatAddress(address) {
  if (!address) return "";

  return address.line1 + ", " + address.postalCode + ", " + address.city;
}

function formatYesNo(condition) {
  return condition ? "Yes" : "No";
}

const restrictTextLength = (input, length) => {
  if (!input) {
    return input;
  }

  if (input.length > length) {
    return input.substring(0, length) + "...";
  }
  return input;
};

const replaceTokens = (tokenValues, template) => {
  // Return empty string if template is undefined or null
  if (!template) {
    return "";
  }

  // Return template as is if tokenValues is undefined or null
  if (!tokenValues) {
    return template;
  }

  // tokenValues = [{ token: "customer", value: "xyz" }, { token: "amount", value: "£1234.45" }]
  let output = template; // Get the value of the specified input

  tokenValues.forEach(({ token, value }) => {
    if (token && value) {
      const regex = new RegExp(`{${token}}`, "g");
      output = output.replace(regex, value);
    }
  });

  return output;
};

function stringReplace(text, needle, replacement) {
  return text.replace(needle, replacement);
}

function formatType(type) {
  if (type.startsWith("Settle")) {
    return stringReplace(type, "Settle", "");
  }
  if (type.startsWith("AddUnallocatedCash")) {
    return "Unallocated Cash";
  }
  if (type.startsWith("ApplyCreditNote")) {
    return "Credit Note";
  }

  return type;
}

// Recursively extract plain text from a React render result
function htmlToPlainText(html) {
  if (!html || !/<[a-zA-Z]/.test(html)) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  let text = "";
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (node.tagName === "BR") {
        text += "\n";
      } else {
        if (
          (node.tagName === "DIV" || node.tagName === "P") &&
          text.length > 0 &&
          !text.endsWith("\n")
        ) {
          text += "\n";
        }
        node.childNodes.forEach(walk);
      }
    }
  };
  doc.body.childNodes.forEach(walk);
  return text;
}

const extractTitle = (node) => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractTitle).join("");
  if (isValidElement(node) && typeof node.type === "string") {
    return extractTitle(node.props.children);
  }
  return "";
};

export {
  applyCountryCode,
  formatDate,
  formatDateLong,
  formatDateShortMonth,
  formatDateTime,
  formatDateTimeForExport,
  formatAmount,
  normalizeAmountValue,
  stringReplace,
  formatKebabCase,
  formatSnakeCase,
  formatSortCode,
  formatAddress,
  formatCountdown,
  formatCommMethod,
  formatPascalCase,
  formatYesNo,
  restrictTextLength,
  formatTimeAgo,
  replaceTokens,
  getMonth,
  formatPaymentSource,
  yesterday,
  formatType,
  extractTitle,
  htmlToPlainText,
};
